import { useState, useEffect } from 'react'

interface ServiceNotice {
  id: number
  title: string
  body: string | null
  severity: 'info' | 'warning' | 'critical'
  /** Flag-driven maintenance notice is non-dismissible; admin notices are. */
  dismissible?: boolean
}

interface Props {
  notices: ServiceNotice[]
  /** 'bar' = full-width strip flush under the navbar (dashboards);
   *  'floating' = rounded card (marketing, floated bottom-center by the layout). */
  variant?: 'bar' | 'floating'
}

// Solid, high-contrast backgrounds — the old bg-*-900/30 (30% opacity) was
// effectively invisible on the dark dashboards. These read clearly everywhere.
const SEVERITY_STYLES: Record<string, { bg: string; label: string; role: string }> = {
  critical: { bg: 'bg-red-600 border-red-400 text-white', label: 'Critical', role: 'alert' },
  warning: { bg: 'bg-amber-400 border-amber-300 text-amber-950', label: 'Notice', role: 'status' },
  info: { bg: 'bg-blue-600 border-blue-400 text-white', label: 'Notice', role: 'status' },
}

export default function ServiceNoticeBanner({ notices, variant = 'floating' }: Props) {
  const [dismissed, setDismissed] = useState<Set<number>>(new Set())

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('dismissed_notices')
      if (stored) setDismissed(new Set(JSON.parse(stored)))
    } catch { /* ignore */ }
  }, [])

  function dismiss(id: number) {
    const next = new Set(dismissed)
    next.add(id)
    setDismissed(next)
    try {
      sessionStorage.setItem('dismissed_notices', JSON.stringify([...next]))
    } catch { /* ignore */ }
  }

  // Non-dismissible notices (maintenance) always show; the rest honour dismissal.
  const visible = notices.filter(n => n.dismissible === false || !dismissed.has(n.id))
  if (visible.length === 0) return null

  const isBar = variant === 'bar'

  return (
    <div className={isBar ? '' : 'space-y-2'}>
      {visible.map(notice => {
        const style = SEVERITY_STYLES[notice.severity] || SEVERITY_STYLES.info
        const canDismiss = notice.dismissible !== false

        return (
          <div
            key={notice.id}
            role={style.role}
            className={[
              style.bg,
              'border px-4 py-3 text-sm flex items-start justify-between gap-3 shadow-lg',
              isBar ? 'border-x-0 border-t-0' : 'rounded-lg',
            ].join(' ')}
          >
            <div>
              <span className="font-semibold mr-2">{style.label}:</span>
              <span className="font-medium">{notice.title}</span>
              {notice.body && <p className="mt-1 text-xs opacity-90">{notice.body}</p>}
            </div>
            {canDismiss && (
              <button
                type="button"
                onClick={() => dismiss(notice.id)}
                className="shrink-0 text-xs font-semibold underline underline-offset-2 opacity-80 hover:opacity-100"
                aria-label={`Dismiss ${notice.title}`}
              >
                Dismiss
              </button>
            )}
          </div>
        )
      })}
    </div>
  )
}
